import React, { useState } from "react";
import {
  DialogContent,
  Grid,
  Typography,
  Box,
  Paper,
  Grow,
  Chip,
  LinearProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import LockIcon from "@mui/icons-material/Lock";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import MDDialog from "components/MDDialog";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { updatePlace, getPlaces, getPlaceById, setSelectedPlace, setSinglePlace } from "store/slice/places/placeSlice";
import CircularIndeterminate from "components/MDLoading";
import PlacePicker from "components/MDPlacePicker/PlacePicker";
import { useEffect } from "react";

const PlaceOnboardingDialog = ({ open, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const [loading, setLoading] = useState(false);

  const selectedPlace = useSelector((state) => state.places?.selectedPlace);
  const placesList = useSelector((state) => state.places?.placesList || []);
  const placeId = selectedPlace?._id;
  const setupData = selectedPlace?.setup?.steps || {};

  // ✅ Refresh place data every time the dialog opens so checklist is always up-to-date
  useEffect(() => {
    if (open) {
      dispatch(getPlaces());
    }
  }, [open, dispatch]);

  const user = useSelector((state) => state.users?.meInfo);

  const isBrandAdmin = user?.roleTitle === "Brand Admin";
  const isSuperAdmin = user?.roleTitle === "Super Admin";

  const skippedData = selectedPlace?.setup?.skipped || {};

  const isSkipped = skippedData?.isSkipped && !skippedData?.isPermanent;
  const isPermanentSkipped = skippedData?.isPermanent;

  const brandAdminSteps = [
    {
      key: "placeSettings",
      label: "Place Settings",
      desc: "Configure basic place settings",
      route: `/places/settings/${placeId}`,
      completed: setupData.placeSettings,
    },
    {
      key: "pricingTierSettings",
      label: "Pricing Tiers",
      desc: "Configure pricing tiers",
      route: `/pricing_tier`,
      completed: setupData.pricingTierSettings,
    },
    {
      key: "ratesCreate",
      label: "Create Rates",
      desc: "Define pricing structure",
      route: `/rates`,
      completed: setupData.ratesCreate,
    },
    {
      key: "ratesAssigned",
      label: "Assign Rates",
      desc: "Assign rates to place",
      route: `/assign_rates`,
      completed: setupData.ratesAssigned,
      disabled: !setupData.ratesCreate,
    },
  ];

  const superAdminSteps = [
    {
      key: "placeSettings",
      label: "Place Settings",
      desc: "Configure place",
      route: `/places/settings/${placeId}`,
      completed: setupData.placeSettings,
    },
    {
      key: "pricingTierSettings",
      label: "Pricing Tiers",
      desc: "Setup pricing tiers",
      route: `/pricing_tier`,
      completed: setupData.pricingTierSettings,
    },
    {
      key: "generalSettings",
      label: "General Settings",
      desc: "Setup general settings",
      route: `/settings?tab=plivoNumber`,
      completed: setupData.generalSettings,
    },
  ];

  const steps = isSuperAdmin
    ? superAdminSteps
    : isBrandAdmin
      ? brandAdminSteps
      : [];

  const isPlaceCompleted =
    steps.length > 0 && steps.every((step) => step.completed === true);

  // Calculate Progress Percentage
  const completedCount = steps.filter((s) => s.completed).length;
  const progress = (completedCount / steps.length) * 100;

  const handleSkipToggle = async (type) => {
    if (!placeId || isPlaceCompleted) return; // ✅ BLOCK if completed

    try {
      setLoading(true);

      const currentSetup = selectedPlace?.setup || {};

      let skippedPayload = {};
      let isSettingSkip = false; // track direction

      if (type === "temporary") {
        isSettingSkip = !isSkipped;
        skippedPayload = isSkipped
          ? { isSkipped: false, isPermanent: false }
          : { isSkipped: true, isPermanent: false };
      }

      if (type === "permanent") {
        isSettingSkip = !isPermanentSkipped;
        skippedPayload = isPermanentSkipped
          ? { isSkipped: false, isPermanent: false } // re-enabling → keep dialog open
          : { isSkipped: true, isPermanent: true };
      }

      await dispatch(
        updatePlace({
          placeId,
          type: "UPDATE",
          setup: {
            ...currentSetup,
            skipped: skippedPayload,
          },
        }),
      ).unwrap();

      await dispatch(getPlaces());

      // ✅ Only close when actually skipping, not when re-enabling
      if (isSettingSkip) {
        onClose();
      }
    } catch (err) {
      console.error("Skip toggle error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MDDialog
      dialogTitle="🚀 Complete Place Setup"
      open={open}
      dialogClose={onClose}
      maxWidth="sm"
      closeIcon={true}
    >
      <CircularIndeterminate
        type="full"
        size={20}
        text="Skipping...!"
        open={loading}
      />
      <DialogContent sx={{ px: 3, pb: 4 }}>
        <Grow in timeout={300}>
          <Box>
            {/* Progress Header */}
            <Box mb={3} mt={1}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="button" fontWeight="bold" color="text">
                  Your Progress
                </Typography>
                <Typography variant="caption" fontWeight="bold" color="text">
                  {Math.round(progress)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 5,
                  backgroundColor: "#f0f2f5",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 5,
                    backgroundImage: `linear-gradient(to right, #30CFD0 0%, #330867 100%)`, // Modern Gradient
                  },
                }}
              />
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: "16px",
                    background: "rgba(248, 249, 250, 0.7)",
                    border: "1px solid rgba(222, 226, 230, 0.5)",
                  }}
                >
                  <PlacePicker fromOnboardingDialog />
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Typography
                  fontSize={15}
                  fontWeight={700}
                  color="dark"
                  mb={2}
                  ml={0.5}
                >
                  Setup Checklist
                </Typography>

                <Box display="flex" flexDirection="column" gap={2}>
                  {steps.map((step) => {
                    const isCompleted = step.completed;
                    const isDisabled = step.disabled;
                    const isActive = !isCompleted && !isDisabled;

                    return (
                      <Box
                        key={step.key}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          p: 2,
                          borderRadius: "12px",
                          background: isCompleted ? "#fcfcfc" : "#fff",
                          border: "1px solid",
                          borderColor: isActive
                            ? `${sidenavColor}.main`
                            : "#eceff1",
                          borderLeftWidth: isActive ? "6px" : "1px",
                          boxShadow: isActive
                            ? "0 4px 12px rgba(0,0,0,0.05)"
                            : "none",
                          transition: "all 0.2s ease-in-out",
                          opacity: isDisabled ? 0.6 : 1,
                          "&:hover": {
                            transform: isDisabled ? "none" : "translateY(-2px)",
                            boxShadow: "0 6px 15px rgba(0,0,0,0.08)",
                          },
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={2}>
                          {isCompleted ? (
                            <CheckCircleIcon
                              color="success"
                              sx={{ fontSize: 28 }}
                            />
                          ) : isDisabled ? (
                            <LockIcon color="disabled" />
                          ) : (
                            <RadioButtonUncheckedIcon color="action" />
                          )}

                          <Box>
                            <Typography
                              fontSize={14}
                              fontWeight={700}
                              color={isCompleted ? "text.secondary" : "dark"}
                            >
                              {step.label}
                            </Typography>
                            <Typography fontSize={12} color="text.secondary">
                              {step.desc}
                            </Typography>
                          </Box>
                        </Box>

                        <Box display="flex" alignItems="center" gap={1.5}>
                          {!isDisabled && (
                            <MDButton
                              size="small"
                              variant={isCompleted ? "outlined" : "gradient"}
                              color={isCompleted ? "success" : sidenavColor}
                              onClick={() => {
                                navigate(step.route);
                                onClose();
                              }}
                              sx={{
                                borderRadius: "8px",
                                textTransform: "none",
                                minHeight: "32px",
                                px: 2,
                              }}
                            >
                              {isCompleted ? "Revisit" : ""}
                              {!isCompleted && (
                                <ArrowForwardIosIcon
                                  sx={{ fontSize: 10, ml: 1 }}
                                />
                              )}
                            </MDButton>
                          )}
                          {isDisabled && (
                            <Chip
                              label="Locked"
                              size="small"
                              icon={<LockIcon fontSize="small" />}
                            />
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Grid>

              <Grid
                item
                xs={12}
                sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
              >
                {/* TEMP SKIP BUTTON */}
                <MDButton
                  variant="outlined"
                  color={isSkipped ? "success" : "secondary"}
                  onClick={() => handleSkipToggle("temporary")}
                  disabled={loading || isPlaceCompleted} 
                  sx={{ textTransform: "none" }}
                >
                  {isSkipped ? "Resume Setup" : "Skip for Now"}
                </MDButton>

                {/* PERMANENT SKIP BUTTON */}
                <MDButton
                  variant="outlined"
                  color={isPermanentSkipped ? "success" : "error"}
                  onClick={() => handleSkipToggle("permanent")}
                  disabled={loading || isPlaceCompleted} 
                  sx={{ textTransform: "none" }}
                >
                  {isPermanentSkipped
                    ? "Enable Setup Again"
                    : "Don't show again for this place"}
                </MDButton>
              </Grid>
            </Grid>
          </Box>
        </Grow>
      </DialogContent>
    </MDDialog>
  );
};

export default PlaceOnboardingDialog;
